import type {
  DatesAtAGlanceRow,
  DatesAtAGlanceTimelineBucket,
} from '@models/interfaces';
import type { DatesAtAGlanceMilestoneStatus } from './dates-at-a-glance.constants';

export interface DatesAtAGlanceTimelineChartBucket
  extends DatesAtAGlanceTimelineBucket {
  scheduledMilestones: number;
}

export const getDatesAtAGlanceScheduledMilestones = ({
  completedMilestones,
  overdueMilestones,
  totalMilestones,
  upcomingMilestones,
}: DatesAtAGlanceTimelineBucket): number =>
  Math.max(
    totalMilestones -
      completedMilestones -
      overdueMilestones -
      upcomingMilestones,
    0,
  );

export const getDatesAtAGlanceTimelineChartData = (
  timelineBuckets: DatesAtAGlanceTimelineBucket[],
): DatesAtAGlanceTimelineChartBucket[] =>
  timelineBuckets.map((bucket: DatesAtAGlanceTimelineBucket) => ({
    ...bucket,
    scheduledMilestones: getDatesAtAGlanceScheduledMilestones(bucket),
  }));

export const hasDatesAtAGlanceTimelineData = (
  timelineBuckets: DatesAtAGlanceTimelineChartBucket[],
): boolean =>
  timelineBuckets.some(
    ({
      completedMilestones,
      overdueMilestones,
      scheduledMilestones,
      upcomingMilestones,
    }: DatesAtAGlanceTimelineChartBucket) =>
      completedMilestones > 0 ||
      overdueMilestones > 0 ||
      scheduledMilestones > 0 ||
      upcomingMilestones > 0,
  );

export const getDatesAtAGlanceMilestoneStatus = ({
  actualDate,
  isOverdue,
  isUpcoming,
}: Pick<
  DatesAtAGlanceRow,
  'actualDate' | 'isOverdue' | 'isUpcoming'
>): DatesAtAGlanceMilestoneStatus => {
  if (actualDate) {
    return 'completed';
  }

  if (isOverdue) {
    return 'overdue';
  }

  if (isUpcoming) {
    return 'upcoming';
  }

  return 'scheduled';
};

export const getDatesAtAGlanceMilestoneStatusClassName = (
  status: DatesAtAGlanceMilestoneStatus,
): string =>
  [
    'prov-dates-at-a-glance-report__status',
    `prov-dates-at-a-glance-report__status--${status}`,
  ].join(' ');
